import React, { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import pb from '../../lib/pb';
import { signOutAdmin } from '../../lib/auth';
import { useData } from '../../data/DataProvider';
import { invoiceTotal, money } from '../formatters';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../icons';
import Skeleton from '../../shared/components/Skeleton';

const CLIENT_ROUTES = ['/admin/clients', '/admin/quotes', '/admin/messages', '/admin/certificates'];
const ENGINEER_PREFIX = '/admin/engineer';

const isClientRoute = (pathname) => CLIENT_ROUTES.some((route) => pathname.startsWith(route));
const isEngineerRoute = (pathname) => pathname.startsWith(ENGINEER_PREFIX);

function NavGroup({ groupKey, label, icon, open, active, badge, onToggle, children }) {
  const contentId = `admin-nav-group-${groupKey}`;
  return (
    <div className={`nav-group${open ? ' open' : ''}${active ? ' active' : ''}`}>
      <button
        type="button"
        className="nav-group-header"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={onToggle}
      >
        <SafeIcon icon={icon} />
        <span>{label}</span>
        {badge}
        <SafeIcon icon={Icons.ChevronDown} className="nav-chevron" />
      </button>
      <div id={contentId} className={`nav-submenu${open ? ' open' : ''}`}>
        <div className="nav-submenu-inner">{children}</div>
      </div>
    </div>
  );
}

export default function AdminShell() {
  const { data, ready } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Start with the group(s) containing the current route expanded (covers
  // deep links and page refresh); the effect below keeps them in sync as the
  // user navigates without collapsing groups opened manually.
  const [openGroups, setOpenGroups] = useState(() => {
    const groups = new Set();
    if (isClientRoute(location.pathname)) groups.add('client');
    if (isEngineerRoute(location.pathname)) groups.add('engineer');
    return groups;
  });

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (isClientRoute(location.pathname)) next.add('client');
      if (isEngineerRoute(location.pathname)) next.add('engineer');
      return next;
    });
  }, [location.pathname]);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const signOut = () => {
    signOutAdmin();
    navigate('/admin/login', { replace: true });
  };

  // Agency-only area — redirect when no valid agency session.
  const session = pb.authStore.record;
  const role = session?.role || null;
  if (role !== 'agency_admin' && role !== 'agency_staff') {
    return <Navigate to="/admin/login" replace />;
  }

  if (!ready) {
    return <div className="admin-frame"><main className="admin-main"><div className="standalone-page"><Skeleton rows={8} /></div></main></div>;
  }

  const unread = data.messages.filter(
    (message) => !message.read_at && message.sender === 'client'
  ).length;

  const outstanding = data.invoices
    .filter((invoice) => invoice.status !== 'paid')
    .reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);

  const newSignups = (data.signups || []).filter((s) => s.status === 'new').length;

  const closeMenu = () => setMobileOpen(false);
  const tenantName = data.tenant.name || 'Client Portal';
  const tenantShort = tenantName.split(' ')[0].toLowerCase();
  const userName = session.name || 'Staff';
  const userInitials = userName.split(' ').map((word) => word[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="admin-frame">
      {mobileOpen && (
        <button
          className="sidebar-overlay"
          aria-label="Close navigation"
          onClick={closeMenu}
        />
      )}

      <aside className={`admin-sidebar ${mobileOpen ? 'sidebar-open' : ''}`}>
        <div className="brand admin-brand">
          <div className="brand-mark">
            <SafeIcon icon={Icons.Droplet} />
          </div>
          <div>
            <strong>{tenantShort}</strong>
            <span>CLIENT PORTAL</span>
          </div>
        </div>

        <nav className="admin-nav" aria-label="Admin navigation">
          <NavLink to="/admin" end className="nav-link" onClick={closeMenu}>
            <SafeIcon icon={Icons.Home} />
            <span>Overview</span>
          </NavLink>

          <NavLink to="/admin/schedule" className="nav-link" onClick={closeMenu}>
            <SafeIcon icon={Icons.Calendar} />
            <span>Schedule</span>
          </NavLink>

          <NavLink to="/admin/jobs" className="nav-link" onClick={closeMenu}>
            <SafeIcon icon={Icons.Tool} />
            <span>Jobs</span>
          </NavLink>

          <NavLink to="/admin/reviews" className="nav-link" onClick={closeMenu}>
            <SafeIcon icon={Icons.Bell} />
            <span>Reviews</span>
          </NavLink>

          <NavLink to="/admin/signups" className="nav-link" onClick={closeMenu}>
            <SafeIcon icon={Icons.Zap} />
            <span>Sign-ups</span>
            {newSignups > 0 && <b className="nav-badge">{newSignups}</b>}
          </NavLink>

          <NavLink to="/admin/settings" className="nav-link" onClick={closeMenu}>
            <SafeIcon icon={Icons.Settings} />
            <span>Settings</span>
          </NavLink>

          <NavGroup
            groupKey="client"
            label="Client"
            icon={Icons.Users}
            open={openGroups.has('client')}
            active={isClientRoute(location.pathname)}
            badge={unread > 0 ? <b className="nav-badge">{unread}</b> : null}
            onToggle={() => toggleGroup('client')}
          >
            <NavLink to="/admin/clients" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.Users} />
              <span>Clients</span>
              <b className="nav-badge">{money(outstanding)}</b>
            </NavLink>

            <NavLink to="/admin/quotes" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.FileText} />
              <span>Quotes</span>
            </NavLink>

            <NavLink to="/admin/messages" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.MessageCircle} />
              <span>Messages</span>
              <b className="nav-badge">{unread}</b>
            </NavLink>

            <NavLink to="/admin/certificates" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.FileText} />
              <span>Certificates</span>
            </NavLink>
          </NavGroup>

          <NavGroup
            groupKey="engineer"
            label="Engineer"
            icon={Icons.Wrench}
            open={openGroups.has('engineer')}
            active={isEngineerRoute(location.pathname)}
            onToggle={() => toggleGroup('engineer')}
          >
            <NavLink to="/admin/engineer/job-sheets" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.Clipboard} />
              <span>Job Sheets</span>
            </NavLink>

            <NavLink to="/admin/engineer/files" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.Archive} />
              <span>Files</span>
            </NavLink>

            <NavLink to="/admin/engineer/mapping" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.Map} />
              <span>Mapping</span>
            </NavLink>

            <NavLink to="/admin/engineer/parts" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.Package} />
              <span>Parts</span>
            </NavLink>

            <NavLink to="/admin/engineer/faults" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.Wrench} />
              <span>Faults</span>
            </NavLink>

            <NavLink to="/admin/engineer/expenses" className="nav-sublink" onClick={closeMenu}>
              <SafeIcon icon={Icons.Cash} />
              <span>Expenses</span>
            </NavLink>
          </NavGroup>
        </nav>

        <div className="sidebar-footer">
          <div className="user-avatar">{userInitials}</div>
          <div>
            <strong>{userName}</strong>
            <span>{role === 'agency_admin' ? 'Agency admin' : 'Agency staff'}</span>
          </div>
          <button
            className="icon-button sidebar-signout"
            aria-label="Sign out"
            title="Sign out"
            onClick={signOut}
          >
            <SafeIcon icon={Icons.LogOut} />
          </button>
        </div>
      </aside>

      <button
        className="mobile-menu icon-button"
        aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen((isOpen) => !isOpen)}
      >
        <SafeIcon icon={mobileOpen ? Icons.X : Icons.Menu} />
      </button>

      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
